import { useMemo, useState } from "react"
import { Check, Info, ShieldCheck, X } from "lucide-react"
import Dialog from "@mui/material/Dialog"
import DialogTitle from "@mui/material/DialogTitle"
import DialogContent from "@mui/material/DialogContent"
import DialogActions from "@mui/material/DialogActions"
import MDButton from "components/MDButton"
import MDInput from "components/MDInput"
import MDSelect from "components/MDSelect"
import MDTypography from "components/MDTypography"
import { DataTable, EmptyState, ErrorState, formatDateTime } from "procurement/design"
import { Button, Card, Page } from "./ui"
import MDNumberInput from "./components/MDNumberInput"
import {
  EMPTY_RESTRICTION_FORM,
  toRestrictionForm,
  toRestrictionPayload,
  validateRestrictionForm,
} from "./components/approvalRestrictions/restrictionForm"
import { formatVNDExact } from "./constants"
import { getApiErrorMessage } from "./orderSubmitFeedback"
import {
  useDeleteApprovalRestrictionMutation,
  useGetApprovalRestrictionRolesQuery,
  useGetApprovalRestrictionsQuery,
  useSaveApprovalRestrictionMutation,
} from "shared/redux/endpoints/procurementV2"
import { showToastError, showToastSuccess } from "utilities/toastUtil"

const RULE_NOTES = [
  "A row applies to a user when it matches their email OR one of their roles.",
  "If several rows match, the highest max value is used.",
  "A user who matches no row has no limit.",
  "Changes take effect immediately, including for documents already awaiting approval.",
]

const NEW_ROW = { key: "new" }

const restrictionTarget = (row) =>
  [row.email, row.roleName].filter(Boolean).join(" / ")

const ApprovalRestrictionsContainer = () => {
  const { data: rows, isLoading, isError, refetch } = useGetApprovalRestrictionsQuery()
  const [saveRestriction, { isLoading: saving }] = useSaveApprovalRestrictionMutation()
  const [deleteRestriction, { isLoading: deleting }] = useDeleteApprovalRestrictionMutation()

  // The one line open for input: { id: null } is the new line at the bottom,
  // a number is an existing row being edited.
  const [editor, setEditor] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)

  const { data: roles } = useGetApprovalRestrictionRolesQuery(undefined, { skip: !editor })
  const roleOptions = useMemo(
    () => (roles || []).map((r) => ({ value: r.roleId, label: r.name })),
    [roles]
  )

  const tableRows = useMemo(
    () => (editor?.id === null ? [...(rows || []), NEW_ROW] : rows || []),
    [rows, editor?.id]
  )

  const isEditing = (row) => !!editor && editor.id === (row.id ?? null)

  const setField = (key, value) =>
    setEditor((prev) => ({
      ...prev,
      form: { ...prev.form, [key]: value },
      errors: { ...prev.errors, [key]: null, target: null },
    }))

  const handleSave = async () => {
    const errors = validateRestrictionForm(editor.form)
    if (Object.keys(errors).length > 0) {
      setEditor((prev) => ({ ...prev, errors }))
      return
    }
    try {
      await saveRestriction({ id: editor.id, ...toRestrictionPayload(editor.form) }).unwrap()
      showToastSuccess(editor.id === null ? "Restriction added." : "Restriction updated.")
      setEditor(null)
    } catch (e) {
      showToastError(getApiErrorMessage(e, "Couldn't save this restriction. Please try again."))
    }
  }

  const handleDelete = async () => {
    try {
      await deleteRestriction(deleteTarget.id).unwrap()
      showToastSuccess("Restriction deleted.")
      setDeleteTarget(null)
    } catch (e) {
      showToastError(getApiErrorMessage(e, "Couldn't delete this restriction. Please try again."))
    }
  }

  const columns = [
    {
      key: "email",
      header: "Email",
      render: (r) =>
        isEditing(r) ? (
          <div style={{ minWidth: 220 }}>
            <MDInput
              fullWidth
              autoFocus
              placeholder="name@example.com"
              inputProps={{ "aria-label": "Email" }}
              value={editor.form.email}
              error={!!(editor.errors.email || editor.errors.target)}
              helperText={editor.errors.email || editor.errors.target}
              onChange={(e) => setField("email", e.target.value)}
              disabled={saving}
            />
          </div>
        ) : (
          r.email || "—"
        ),
    },
    {
      key: "roleName",
      header: "Role",
      render: (r) =>
        isEditing(r) ? (
          <div style={{ minWidth: 200 }}>
            <MDSelect
              isClearable
              // The table scroller would clip the menu on the last rows.
              menuPortalTarget={document.body}
              menuPosition="fixed"
              menuPlacement="auto"
              aria-label="Role"
              placeholder="Select a role"
              options={roleOptions}
              value={roleOptions.find((o) => o.value === editor.form.roleId) || null}
              onChange={(o) => setField("roleId", o?.value ?? null)}
              isDisabled={saving}
            />
          </div>
        ) : (
          r.roleName || "—"
        ),
    },
    {
      key: "maxValue",
      header: "Max value",
      align: "right",
      strong: true,
      render: (r) =>
        isEditing(r) ? (
          <div style={{ minWidth: 160 }}>
            <MDNumberInput
              fullWidth
              placeholder="e.g. 50,000,000"
              inputProps={{ "aria-label": "Max value" }}
              value={editor.form.maxValue}
              error={!!editor.errors.maxValue}
              helperText={editor.errors.maxValue}
              onChange={(v) => setField("maxValue", v)}
              disabled={saving}
            />
          </div>
        ) : (
          formatVNDExact(r.maxValue)
        ),
    },
    { key: "updatedAt", header: "Updated", render: (r) => formatDateTime(r.updatedAt) },
    {
      key: "actions",
      header: "",
      className: "pc-col-actions",
      render: (r) =>
        isEditing(r) ? (
          <span style={{ display: "inline-flex", gap: 8 }}>
            <Button
              size="sm"
              variant="primary"
              aria-label={r.id ? "Save" : "Add"}
              onClick={handleSave}
              disabled={saving}
            >
              <Check aria-hidden />
            </Button>
            <Button
              size="sm"
              variant="secondary"
              aria-label="Cancel"
              onClick={() => setEditor(null)}
              disabled={saving}
            >
              <X aria-hidden />
            </Button>
          </span>
        ) : (
          <span style={{ display: "inline-flex", gap: 8 }}>
            <Button
              size="sm"
              variant="secondary"
              disabled={!!editor}
              onClick={() => setEditor({ id: r.id, form: toRestrictionForm(r), errors: {} })}
            >
              Edit
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={!!editor}
              onClick={() => setDeleteTarget(r)}
            >
              Delete
            </Button>
          </span>
        ),
    },
  ]

  return (
    <Page
      className="pc-page--refined pc-page--list"
      breadcrumb={["Procurement Process", "Approval Restriction"]}
      title="Approval Restriction"
      subtitle="Set the highest document value a user can approve across Orders, POs and Payment Requests."
      actions={
        <Button
          variant="primary"
          disabled={!!editor}
          onClick={() => setEditor({ id: null, form: EMPTY_RESTRICTION_FORM, errors: {} })}
        >
          New restriction
        </Button>
      }
    >
      <Card icon={<Info aria-hidden />} title="How restrictions apply">
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
          {RULE_NOTES.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      </Card>

      {isError ? (
        <ErrorState
          title="Couldn't load approval restrictions"
          action={
            <Button variant="secondary" onClick={refetch}>
              Try again
            </Button>
          }
        />
      ) : (
        <DataTable
          columns={columns}
          rows={tableRows}
          isLoading={isLoading}
          stickyFirst={false}
          // Paging would hide the new line at the bottom; the list is unpaged and short.
          hidePagination
          empty={
            <EmptyState
              icon={<ShieldCheck aria-hidden />}
              title="No restrictions yet"
              text="Every approver is currently unlimited."
            />
          }
        />
      )}

      <Dialog
        open={!!deleteTarget}
        onClose={() => !deleting && setDeleteTarget(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Delete restriction?</DialogTitle>
        <DialogContent>
          <MDTypography variant="button" color="text" fontWeight="regular">
            The limit of {formatVNDExact(deleteTarget?.maxValue)} for{" "}
            <strong>{deleteTarget ? restrictionTarget(deleteTarget) : ""}</strong> will be
            permanently removed. This takes effect immediately.
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton
            variant="outlined"
            color="dark"
            onClick={() => setDeleteTarget(null)}
            disabled={deleting}
          >
            Cancel
          </MDButton>
          <MDButton variant="gradient" color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? "Deleting…" : "Delete"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </Page>
  )
}

export default ApprovalRestrictionsContainer
